import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { GripVertical, CheckCircle, Type, Hash, Camera, List, Activity, Pencil, Trash2, MapPin } from "lucide-react";
import { useLanguage } from "@/core/LanguageContext";
import { ALL_LANGUAGES } from "@/shared/config/languages";
import type { ChecklistItem, ItemType } from "./types";

const itemTypeIcons: Record<ItemType, React.ReactNode> = {
  yes_no: <CheckCircle className="w-4 h-4" />,
  text: <Type className="w-4 h-4" />,
  number: <Hash className="w-4 h-4" />,
  photo: <Camera className="w-4 h-4" />,
  dropdown: <List className="w-4 h-4" />,
  field_lookup: <Activity className="w-4 h-4" />,
};

const itemTypeLabels: Record<ItemType, string> = {
  yes_no: "Yes/No",
  text: "Text Input",
  number: "Number",
  photo: "Photo",
  dropdown: "Dropdown",
  field_lookup: "Field Lookup",
};

interface ItemRowProps {
  item: ChecklistItem;
  index: number;
  isDragging: boolean;
  isDropTarget: boolean;
  assignmentLabel: string | null;
  onEdit: () => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent, itemId: string) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
}

export function ItemRow({
  item,
  index,
  isDragging,
  isDropTarget,
  assignmentLabel,
  onEdit,
  onDelete,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: ItemRowProps) {
  const { secondaryLanguage } = useLanguage();

  return (
    <div>
      {/* Drop indicator before item */}
      <div
        className={`transition-all duration-200 ease-out ${isDropTarget ? 'h-16 bg-primary/10 border-2 border-dashed border-primary rounded-lg my-2' : 'h-0'}`}
      />
      <div
        draggable
        onDragStart={(e) => onDragStart(e, item.id)}
        onDragOver={(e) => onDragOver(e, index)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, index)}
        onDragEnd={onDragEnd}
        className={`transition-all duration-200 mb-2 ${isDragging ? 'opacity-40 scale-[0.98]' : 'opacity-100'}`}
      >
        <Card className={`overflow-hidden ${isDragging ? 'ring-2 ring-primary' : ''}`}>
          <CardContent className="py-3 px-4 flex items-center gap-3 min-w-0">
            <div className="cursor-grab active:cursor-grabbing touch-none shrink-0">
              <GripVertical className="w-4 h-4 text-muted-foreground" />
            </div>
            <span className="text-sm text-muted-foreground w-6 shrink-0">{index + 1}.</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium truncate">{item.label}</span>
                {item.is_required && <Badge variant="secondary" className="text-xs shrink-0">Required</Badge>}
                {secondaryLanguage && item.label_translations?.[secondaryLanguage] && (
                  <Badge variant="outline" className="text-xs shrink-0">
                    {ALL_LANGUAGES.find(l => l.code === secondaryLanguage)?.flag}
                  </Badge>
                )}
                {assignmentLabel && (
                  <Badge variant="outline" className="text-xs gap-1 shrink-0">
                    <MapPin className="w-3 h-3" />
                    {assignmentLabel}
                  </Badge>
                )}
              </div>
              {item.description && (
                <p className="text-sm text-muted-foreground truncate">{item.description}</p>
              )}
            </div>
            <Badge variant={item.is_inspection ? "default" : "secondary"} className="gap-1 shrink-0">
              {item.is_inspection ? (
                <>
                  {itemTypeIcons[item.item_type]}
                  {itemTypeLabels[item.item_type]}
                  {item.item_type === 'field_lookup' && item.field_mappings?.length > 1 && (
                    <span className="opacity-70">({item.field_mappings.length})</span>
                  )}
                </>
              ) : (
                <>
                  <CheckCircle className="w-3 h-3" />
                  Instruction
                </>
              )}
            </Badge>
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="icon" onClick={onEdit}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (confirm("Delete this item?")) {
                    onDelete();
                  }
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
